import { useCallback, type RefObject } from "react";
import { FOCUSABLE_ELEMENT_SELECTOR } from "../../constants/constants";

// Extend FocusOptions to include focusVisible (newer browser feature not yet in TS lib)
interface ExtendedFocusOptions extends FocusOptions {
  focusVisible?: boolean;
}

// Module-level store for question refs (internal to this module)
const questionRefsStore: Record<string, RefObject<HTMLDivElement | null>> = {};

// Module-level store for button refs (internal to this module)
const buttonRefsStore: Record<string, HTMLButtonElement | null> = {};

// Helper function to create a ref (outside of React context)
const createRef = (): RefObject<HTMLDivElement | null> => {
  return { current: null };
};

// Single hook that handles both registration and access to question refs
export const useQuestionRefs = () => {
  // Function to register a question ref
  const registerQuestionRef = useCallback((questionName: string) => {
    const ref = createRef();
    questionRefsStore[questionName] = ref;
    return ref;
  }, []);

  // Function to unregister a question ref
  const unregisterQuestionRef = useCallback((questionName: string) => {
    delete questionRefsStore[questionName];
  }, []);

  // Function to get a specific question's ref
  const getQuestionRef = useCallback((name: string) => {
    return questionRefsStore[name];
  }, []);

  // Function to focus a question by finding and focusing its first focusable element
  const focusQuestion = useCallback((name: string) => {
    const questionRef = questionRefsStore[name];
    if (questionRef?.current) {
      const focusableElement = questionRef.current.querySelector(
        FOCUSABLE_ELEMENT_SELECTOR,
      );
      if (focusableElement instanceof HTMLElement) {
        // Use focusVisible option to show focus ring for accessibility
        // preventScroll prevents default scroll so we can use smooth scrolling
        focusableElement.focus({
          focusVisible: true,
          preventScroll: true,
        } as ExtendedFocusOptions);
        // Scroll element into view with smooth behavior if supported
        if (focusableElement.scrollIntoView) {
          focusableElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      } else {
        questionRef.current.focus({
          focusVisible: true,
          preventScroll: true,
        } as ExtendedFocusOptions);
        if (questionRef.current.scrollIntoView) {
          questionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  }, []);

  // Function to focus the first question that has an error
  const focusFirstError = useCallback(
    (errors: Record<string, unknown>) => {
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        const firstErrorKey = errorKeys[0];
        focusQuestion(firstErrorKey);
      }
    },
    [focusQuestion],
  );

  // Generic function to register any button ref
  const registerButtonRef = useCallback(
    (key: string, buttonElement: HTMLButtonElement | null) => {
      buttonRefsStore[key] = buttonElement;
    },
    [],
  );

  // Generic function to unregister any button ref
  const unregisterButtonRef = useCallback((key: string) => {
    delete buttonRefsStore[key];
  }, []);

  // Generic function to get any button ref
  const getButtonRef = useCallback((key: string) => {
    return buttonRefsStore[key] ?? null;
  }, []);

  return {
    // Function to create and register a ref for a question
    registerQuestionRef,
    // Function to unregister a ref (for cleanup)
    unregisterQuestionRef,
    // Access to all registered question refs
    questionRefs: questionRefsStore,
    // Get a specific question's ref
    getQuestionRef,
    // Focus a question by finding and focusing its first focusable element
    focusQuestion,
    // Focus the first question that has an error
    focusFirstError,
    // Generic function to register any button ref
    registerButtonRef,
    // Generic function to unregister any button ref
    unregisterButtonRef,
    // Generic function to get any button ref
    getButtonRef,
  };
};
